import React, { createContext, ReactNode, useEffect, useMemo } from "react";
import { ConfigProvider, theme } from "antd";
import type { ThemeConfig } from "antd";
import type { Locale } from "antd/es/locale";
import { useTranslation } from "react-i18next";
import enUS from "antd/locale/en_US";
import deDE from "antd/locale/de_DE";
import bgBG from "antd/locale/bg_BG";
import { useLocalStorage } from "@/hooks/useLocalStorage";

// Map i18n language codes to Ant Design locale objects
const antdLocaleMap: Record<string, Locale> = {
  en: enUS,
  de: deDE,
  bg: bgBG,
};

// Props interface for AntdThemeProvider
interface AntdThemeProviderProps {
  children: ReactNode;
  themeConfig?: ThemeConfig;
}

// Default theme mode
const isDarkDefault = false;

// Destructure theme algorithms from antd
const { defaultAlgorithm, darkAlgorithm } = theme;

// Default theme configuration
const defaultThemeConfig: ThemeConfig = {
  token: {
    colorPrimary: "#3b82f6",
  },
  components: {
    Layout: {
      // siderBg: "#3b82f6", // Uncomment to customize sider background color
    },
  },
};

// App-wide pagination defaults: always offer the page-size picker (antd's default options are 10/20/50/100).
// Per-table `showTotal` stays on each table, since ConfigProvider cannot set it.
const paginationConfig = { showSizeChanger: true };

// Create ThemeContext with default values
export const ThemeContext = createContext<{
  themeConfig: ThemeConfig;
  isDarkMode: boolean;
  setIsDarkMode: (value: boolean) => void;
}>({
  themeConfig: defaultThemeConfig,
  isDarkMode: isDarkDefault,
  setIsDarkMode: (value: boolean) => {
    console.warn("setIsDarkMode is not implemented");
  },
});

export const AntdThemeProvider: React.FC<AntdThemeProviderProps> = ({ children, themeConfig }) => {
  // Use local storage to persist dark mode state
  const [isDarkMode, setIsDarkMode] = useLocalStorage("isDarkMode", isDarkDefault);

  // Get current i18n language for Ant Design locale
  const { i18n } = useTranslation();
  const antdLocale = useMemo(() => antdLocaleMap[i18n.language] ?? enUS, [i18n.language]);

  // Merge default theme config with provided theme config and set algorithm based on dark mode
  const mergedThemeConfig = {
    ...defaultThemeConfig,
    ...themeConfig,
    algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
  };

  const colorPrimary = mergedThemeConfig.token?.colorPrimary ?? defaultThemeConfig.token?.colorPrimary ?? '#3b82f6';

  // Set data-theme attribute on document for CSS variable support
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.style.setProperty('--client-primary', String(colorPrimary));
  }, [isDarkMode, colorPrimary]);

  return (
    <ThemeContext.Provider value={{ themeConfig: mergedThemeConfig, isDarkMode, setIsDarkMode }}>
      <ConfigProvider theme={mergedThemeConfig} locale={antdLocale} pagination={paginationConfig}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};
